import * as THREE from 'three/webgpu';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import * as TSL from 'three/tsl';
import Rand, { PRNG } from 'rand-seed';
import { SimplexNoise } from 'three/addons/math/SimplexNoise.js';

const texLoader = new THREE.TextureLoader();

const windNoiseTexture = texLoader.load('/tex/Perlin 4 - 256x256.png');
windNoiseTexture.wrapS = windNoiseTexture.wrapT = THREE.RepeatWrapping;
// console.log(windNoiseTexture);

const alphaTexture = texLoader.load('/tex/leave.png');

export class Bush extends THREE.Mesh {
    constructor({
            branchCount = 80,
            radius = 1,
            seed = null,
        } = {}) {
        super();

        this.type = 'Bush';

        this.geometry = createGeometry();
        this.material = createMaterial();

        function createGeometry() {
            const count = 80;
            const planes = [];

            const rand = new Rand(seed);

            for( let i = 0; i < branchCount; i++) {
                const plane = new THREE.PlaneGeometry(0.8,0.5);
                planes.push( plane );

                // Position
                const spherical = new THREE.Spherical(
                    (1 - Math.pow( rand.next(), 3)) * radius,
                    2 * Math.PI * rand.next(),
                    Math.PI * rand.next(),
                );
                const position = new THREE.Vector3().setFromSpherical( spherical );
                // plane.rotateX( Math.PI / 2 * ( Math.random() ));
                plane.rotateY( 2 * Math.PI * rand.next() );
                plane.rotateZ( Math.PI / 4 * rand.next() );
                plane.translate(
                    position.x,
                    position.y,
                    position.z
                );

                // Normal
                const normal = position.clone().normalize();
                const normalArray = new Float32Array(12);

                for (let i = 0; i < 4 ; i++) { // scan all vertex
                    const i3 = i * 3;

                    const position = new THREE.Vector3(
                        plane.attributes.position.array[i3],
                        plane.attributes.position.array[i3 + 1],
                        plane.attributes.position.array[i3 + 2]
                    )
                    const mixedNormal = position.lerp(normal, 0.2);

                    normalArray[i3] = mixedNormal.x;
                    normalArray[i3+1] = mixedNormal.y;
                    normalArray[i3+2] = mixedNormal.z;

                }
                plane.setAttribute('normal', new THREE.BufferAttribute(normalArray, 3));
            }
            const geometry = BufferGeometryUtils.mergeGeometries( planes );
            geometry.translate(0, radius, 0);
            return geometry;
        }

        function createMaterial() {
            const alphaTexture = new THREE.TextureLoader().load('/tex/leave.png');
            return new THREE.MeshMatcapMaterial({
                color: 0x11aa44,
                transparent: true,
                alphaMap: alphaTexture,
                alphaTest: 0.5,
                side: THREE.DoubleSide,
            });
        }
    }
}

export class BushField extends THREE.Object3D {
    constructor(width = 10, height = 10, {
            branchCount = 80,
            radius = 1,
            seed = null,
        }  = {}) {

        super();

        this.type = 'BushField';
        this.width = width;
        this.height = height;
        this.seed = seed;

        const rand = new Rand(seed);

        const r = {
            random: () => {
                const randomValue = rand.next();
                // console.log(randomValue);
                return randomValue;
            }
        }

        this.SN = new SimplexNoise(r);

        this.geometry = createGeometry();
        this.material = createMaterial();
        addWind(this.material);

        const count = this.width * this.height;
        this.mesh = new THREE.InstancedMesh(
            this.geometry,
            this.material,
            count
        );
        this.add(this.mesh);

        // mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
        this.createInstance();


        function createGeometry() {
            const planes = [];
            const rand = new Rand(seed);

            for( let i = 0; i < branchCount; i++) {
                const plane = new THREE.PlaneGeometry(0.8,0.5);
                planes.push( plane );

                // Position
                const spherical = new THREE.Spherical(
                    (1 - Math.pow( rand.next(), 3)) * radius,
                    2 * Math.PI * rand.next(),
                    Math.PI * rand.next(),
                );
                const position = new THREE.Vector3().setFromSpherical( spherical );
                plane.rotateX( Math.PI / 2 * ( rand.next() ));
                plane.rotateY( 2 * Math.PI * rand.next() );
                plane.rotateZ( Math.PI / 4 * rand.next() );
                plane.translate(
                    position.x,
                    position.y,
                    position.z
                );

                // Normal
                const normal = position.clone().normalize();
                const normalArray = new Float32Array(12);

                for (let i = 0; i < 4 ; i++) { // scan all vertex
                    const i3 = i * 3;

                    const position = new THREE.Vector3(
                        plane.attributes.position.array[i3],
                        plane.attributes.position.array[i3 + 1],
                        plane.attributes.position.array[i3 + 2]
                    )
                    const mixedNormal = position.lerp(normal, 0.2);

                    normalArray[i3] = mixedNormal.x;
                    normalArray[i3+1] = mixedNormal.y;
                    normalArray[i3+2] = mixedNormal.z;

                }
                plane.setAttribute('normal', new THREE.BufferAttribute(normalArray, 3));
            }
            const geometry = BufferGeometryUtils.mergeGeometries( planes );
            geometry.translate(0, radius, 0);
            return geometry;
        }

        function createMaterial() {
            // const alphaTexture = new THREE.TextureLoader().load('/tex/leave.png');
            return new THREE.MeshMatcapNodeMaterial({
                color: 0x11aa44,
                transparent: true,
                alphaMap: alphaTexture,
                alphaTest: 0.5,
                side: THREE.DoubleSide,
            });
        }

        function addWind(material) {
            const noiseUV = TSL.positionWorld.xz.mul(0.2).add(TSL.time.mul(0.1));
            const noiseColor = TSL.texture(windNoiseTexture, noiseUV).sub(0.5).mul(TSL.positionWorld.y);
            material.positionNode = TSL.positionLocal.add(TSL.vec3(noiseColor.r, 0 , noiseColor.r));
            // material.positionNode = TSL.positionLocal.add(TSL.vec3(0.1,0,0).mul(TSL.time));
        }
    }

    createInstance() {
            const dummy = new THREE.Object3D();
            const offsetX = (this.width - 1) / 2;
            const offsetZ = (this.height - 1) / 2;
            let i = 0;

            const rand = new Rand(this.seed);

            for (let x = 0; x < this.width; x++) {
                for (let z = 0; z < this.height; z++) {
                    const value = this.SN.noise( x / 5, z / 5 );
                    // console.log(value)
                    dummy.position.set( offsetX - x + rand.next(), 0, offsetZ - z + rand.next() );
                    dummy.rotation.y = Math.PI * rand.next();
                    dummy.scale.setScalar( rand.next() * 0.8 + 0.2 );

                    dummy.updateMatrix();
                    if (value > 0.5) {
                        this.mesh.setMatrixAt( i, dummy.matrix );
                        i++;
                    }
                }
            }
            // console.log(mesh.count, i);
            this.mesh.count = i; // update instance count for optimization
            // console.log(`${this.mesh.count} instances created`);
        }

    dispose() {
        this.mesh.dispose();
        this.geometry.dispose();
        this.material.dispose();
    }
}